import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { CalendarCheckIcon, ClockIcon, GlobeIcon, MapPinIcon, MessageSquareIcon, RepeatIcon, ShieldCheckIcon, ZapIcon } from 'lucide-react';
import { AppShell } from '../../components/layout/AppShell';
import { Card, SectionHeading, EmptyState } from '../../components/ui/Panels';
import { Badge } from '../../components/ui/Badge';
import { Rating } from '../../components/ui/Rating';
import { ButtonLink } from '../../components/ui/Button';
import { SaveButton } from '../../components/chefs/ChefCard';
import { MenuCard } from '../../components/chefs/MenuCard';
import { GalleryGrid } from '../../components/chefs/GalleryGrid';
import { VerificationList } from '../../components/chefs/VerificationList';
import { ReviewList, ScoreBreakdown } from '../../components/chefs/ReviewList';
import { ServiceAreaMap } from '../../components/chefs/ServiceAreaMap';
import { chefById } from '../../data/chefs';
import { reviewsForChef } from '../../data/reviews';
import { cuisineLabel } from '../../data/taxonomy';
import { fromPricePerGuest, fromPriceUnit, signatureMenu } from '../../utils/pricing';
import { currency, longDate, responseTime } from '../../utils/format';
import { useLocale } from '../../contexts/LocaleContext';
import { useAppData } from '../../contexts/AppDataContext';
import { spring } from '../../utils/motion';
const addOnUnitLabel: Record<string, string> = {
  per_guest: 'per guest',
  per_hour: 'per hour',
  flat: 'flat',
  per_km: 'per km'
};
const cancellationCopy: Record<string, string> = {
  flexible: 'Flexible — full deposit refund up to 48 hrs before',
  moderate: 'Moderate — full refund up to 7 days before',
  strict: 'Strict — deposit non-refundable once accepted'
};
export function ChefProfile() {
  const {
    slug
  } = useParams();
  const reduce = useReducedMotion();
  const {
    locale,
    currencyCode,
    t
  } = useLocale();
  const {
    threads
  } = useAppData();
  const chef = chefById(slug ?? '');
  if (!chef) {
    return <AppShell title="Chef not found" data-id="element-1792" data-mp="o5bbkdj13mb">
        <EmptyState icon={<MapPinIcon className="h-5 w-5" aria-hidden="true" data-id="element-1794" data-mp="r18v7lgpqa8" />} title="That listing is no longer live" body="The chef may have paused their profile. There are plenty of others cooking nearby." action={<ButtonLink to="/discover" data-id="element-1795" data-mp="3i4wzcrhi94qp">Back to search</ButtonLink>} data-id="element-1793" data-mp="p70esrdnqn1" />
      </AppShell>;
  }
  const reviews = reviewsForChef(chef.id);
  const signature = signatureMenu(chef);
  const price = fromPricePerGuest(chef);
  const unit = fromPriceUnit(chef);
  const existingThread = threads.find(th => th.chefId === chef.id);
  const inHome = chef.menus.filter(m => m.fulfillment.includes('in_home'));
  const delivery = chef.menus.filter(m => !m.fulfillment.includes('in_home'));
  return <AppShell title={chef.name} subtitle={`${chef.neighbourhood} · ${chef.city}`} data-id="element-1796" data-mp="12eagzaipugud">
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-1797" data-mp="19xjoszbkq7hb">
        <div className="min-w-0" data-id="element-1798" data-mp="19x9x97avrs9i">
          <motion.div initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={reduce ? {
          duration: 0.2
        } : spring.heavy} className="relative overflow-hidden rounded-2xl border border-hairline" data-id="element-1799" data-mp="1ewmrid4301m4">
            <img src={chef.photo} alt={chef.name} className="aspect-[16/9] w-full object-cover" data-id="element-1800" data-mp="qyxp9ovpgofi" />
            <SaveButton chefId={chef.id} className="absolute right-4 top-4" data-id="element-1801" data-mp="vxir1sqlvg62" />
            {chef.bookingMode === 'instant' && <Badge tone="accent" className="absolute left-4 top-4" data-id="element-1802" data-mp="10vsn05k5mksq">
                <ZapIcon className="h-3 w-3" aria-hidden="true" data-id="element-1803" data-mp="14yyn0vgobfqr" /> Instant book
              </Badge>}
          </motion.div>

          <div className="mt-6" data-id="element-1804" data-mp="2cb8tyw5znye4">
            <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-1805" data-mp="2ced4scdylsdr">
              <div data-id="element-1806" data-mp="2gbsr3zqkb209">
                <h2 className="font-display text-3xl font-semibold tracking-[-0.02em] text-ink" data-id="element-1807" data-mp="2gfgmz5jhc0i5">{chef.name}</h2>
                <p className="mt-1.5 max-w-[56ch] text-lg leading-snug text-ink-2" data-id="element-1808" data-mp="29xdhkqoka3mw">{chef.headline}</p>
              </div>
              <Rating value={chef.rating} count={chef.reviewCount} className="text-base" data-id="element-1809" data-mp="2ouwdccznph3u" />
            </div>

            <ul className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-ink-2" data-id="element-1810" data-mp="2jui3cpehvx36">
              <li className="flex items-center gap-1.5" data-id="element-1811" data-mp="2xu9cocu2gpau">
                <MapPinIcon className="h-4 w-4 text-ink-3" aria-hidden="true" data-id="element-1812" data-mp="2sw7urv8dmw0y" /> {chef.neighbourhood}
              </li>
              <li className="flex items-center gap-1.5" data-id="element-1813" data-mp="37lnj74kfqpdh">
                <ClockIcon className="h-4 w-4 text-ink-3" aria-hidden="true" data-id="element-1814" data-mp="37n6z2u26iyeq" /> replies in {responseTime(chef.responseMinutes)}
              </li>
              <li className="flex items-center gap-1.5" data-id="element-1815" data-mp="3glalrtowi184">
                <RepeatIcon className="h-4 w-4 text-ink-3" aria-hidden="true" data-id="element-1816" data-mp="3gjpss8i8iqcd" /> {chef.repeatRate}% book again
              </li>
              <li className="flex items-center gap-1.5" data-id="element-1817" data-mp="3v8r4e6vkcjnw">
                <GlobeIcon className="h-4 w-4 text-ink-3" aria-hidden="true" data-id="element-1818" data-mp="3v9akrj5kezzt" /> {chef.languages.join(', ')}
              </li>
              <li data-id="element-1819" data-mp="3m7t9ce33rmhg">{chef.yearsPro} years cooking professionally</li>
            </ul>

            <ul className="mt-4 flex flex-wrap gap-1.5" data-id="element-1820" data-mp="3m9x9qm1sm3cz">
              {chef.cuisineIds.map(id => <li key={id} className="rounded-full bg-oat px-3 py-1 text-[13px] font-medium text-ink-2" data-id="element-1821" data-mp="3r8qqcncdmahd">
                  {cuisineLabel(id)}
                </li>)}
            </ul>

            <p className="mt-6 max-w-[70ch] text-[15px] leading-relaxed text-ink-2" data-id="element-1822" data-mp="3hakp87ye98dk">{chef.bio}</p>
          </div>

          <section aria-labelledby="signature-menu" className="mt-12" data-id="element-1823" data-mp="37iwspsmzd158">
            <SectionHeading level={2} title="The signature" hint="What this chef would cook if you let them choose." data-id="element-1824" data-mp="37j6m7q78hqfo" />
            <div className="mt-5" data-id="element-1825" data-mp="2tli0yh998ha3">
              <MenuCard menu={signature} chefSlug={chef.slug} defaultOpen data-id="element-1826" data-mp="2yii68pya5g05" />
            </div>
          </section>

          <section aria-labelledby="menus" className="mt-12" data-id="element-1827" data-mp="1431s3e11vesy">
            <SectionHeading level={2} title="Menus & rates" hint="Priced per guest, with a minimum head count and the hours included." data-id="element-1828" data-mp="zbjumzy8jwcq" />
            {!!inHome.length && <>
                <h3 className="mt-6 text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1829" data-mp="qalq8gjvxmtm">Cooked at your place</h3>
                <div className="mt-3 space-y-4" data-id="element-1830" data-mp="1n15ttnelybkq">
                  {inHome.map(menu => <MenuCard key={menu.id} menu={menu} chefSlug={chef.slug} data-id="element-1831" data-mp="1iyr6or3wopnn" />)}
                </div>
              </>}
            {!!delivery.length && <>
                <h3 className="mt-8 text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1832" data-mp="191wbah2xj5dl">Cooked at theirs, delivered</h3>
                <div className="mt-3 space-y-4" data-id="element-1833" data-mp="2mc7jnhyxf6p">
                  {delivery.map(menu => <MenuCard key={menu.id} menu={menu} chefSlug={chef.slug} data-id="element-1834" data-mp="3tpr6dj43l53s" />)}
                </div>
              </>}

            <Card className="mt-6 p-5" data-id="element-1835" data-mp="3om8exk281e9x">
              <h3 className="font-display text-lg font-semibold text-ink" data-id="element-1836" data-mp="3on01k5duc1o2">Add-ons</h3>
              <p className="mt-1 text-sm text-ink-2" data-id="element-1837" data-mp="3tkjodtoltmrm">Each of these becomes its own line on your quote — nothing is bundled invisibly.</p>
              <ul className="mt-4 divide-y divide-hairline" data-id="element-1838" data-mp="2907tx5whozb">
                {chef.addOns.map(addOn => <li key={addOn.id} className="flex items-start justify-between gap-4 py-2.5" data-id="element-1839" data-mp="6av42n8z5c68">
                    <span data-id="element-1840" data-mp="b9ew95e5vk71">
                      <span className="block text-sm font-medium text-ink" data-id="element-1841" data-mp="g65aroabi7qc">{addOn.name}</span>
                      <span className="block text-[13px] text-ink-2" data-id="element-1842" data-mp="l5hyeb90jitz">{addOn.description}</span>
                    </span>
                    <span className="tnum shrink-0 text-right text-sm font-medium text-ink" data-id="element-1843" data-mp="2hz37vwb4nmh3">
                      {currency(addOn.price, locale, currencyCode)}
                      <span className="block text-[12px] font-normal text-ink-3" data-id="element-1844" data-mp="spam7b7ynnsb">{addOnUnitLabel[addOn.unit]}</span>
                    </span>
                  </li>)}
              </ul>
              <dl className="mt-4 grid gap-3 border-t border-hairline pt-4 sm:grid-cols-3" data-id="element-1845" data-mp="12f3x0a0a3yvi">
                <div data-id="element-1846" data-mp="12gwylaw1bswn">
                  <dt className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1847" data-mp="xfhaf9yk96a1">Minimum spend</dt>
                  <dd className="tnum mt-0.5 text-sm font-medium text-ink" data-id="element-1848" data-mp="9rhjoa191fvg">{currency(chef.minimumSpend, locale, currencyCode)}</dd>
                </div>
                <div data-id="element-1849" data-mp="4togzqz5n3zx">
                  <dt className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1850" data-mp="iqusd0hju0f4">Travel</dt>
                  <dd className="mt-0.5 text-sm font-medium text-ink" data-id="element-1851" data-mp="eozt01j6hx3z">
                    {chef.travelIncludedKm} km included, then {currency(chef.travelPerKm, locale, currencyCode)}/km
                  </dd>
                </div>
                <div data-id="element-1852" data-mp="1q450diu98jtx">
                  <dt className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1853" data-mp="1q6r5lbtoan8z">Deposit</dt>
                  <dd className="tnum mt-0.5 text-sm font-medium text-ink" data-id="element-1854" data-mp="1xqjygq7h9ie6">{chef.depositPercent}% to confirm</dd>
                </div>
              </dl>
            </Card>
          </section>

          <section aria-labelledby="gallery" className="mt-12" data-id="element-1855" data-mp="1ns448ed5lt0g">
            <SectionHeading level={2} title="From past tables" data-id="element-1856" data-mp="1spvznid0bx9e" />
            <div className="mt-5" data-id="element-1857" data-mp="2ge5eu1fptmrk">
              <GalleryGrid items={chef.gallery} data-id="element-1858" data-mp="2gg005guwfgw6" />
            </div>
          </section>

          <section aria-labelledby="areas" className="mt-12" data-id="element-1859" data-mp="2lcqhe7ifwij6">
            <SectionHeading level={2} title="Where they cook" hint="Solid rings include travel. Dashed rings add mileage to the quote." data-id="element-1860" data-mp="26ovq8vk2ym87" />
            <div className="mt-5 grid gap-5 lg:grid-cols-[1fr_260px]" data-id="element-1861" data-mp="2bmx85d5rsfi3">
              <ServiceAreaMap areas={chef.serviceAreas} center={{
              lat: chef.lat,
              lng: chef.lng
            }} className="h-72 lg:h-80" data-id="element-1862" data-mp="2zd18nbnnvz4v" />
              <ul className="space-y-3" data-id="element-1863" data-mp="345b3n63mvpe0">
                {chef.serviceAreas.map(area => <li key={area.id} className="rounded-xl border border-hairline bg-surface p-4" data-id="element-1864" data-mp="36dr91qulfstn">
                    <p className="text-sm font-semibold text-ink" data-id="element-1865" data-mp="3ad9ssjaohxuw">{area.name}</p>
                    <p className="mt-1 text-[13px] text-ink-2" data-id="element-1866" data-mp="2wf1rjz6m0adx">{area.districts.join(' · ')}</p>
                    <p className="mt-2 text-[12px] text-ink-3" data-id="element-1867" data-mp="31d3jzapxdm22">
                      {area.fulfillment.includes('in_home') ? 'Cooks here' : 'Delivery only'}
                      {' · '}
                      {area.travelIncluded ? 'travel included' : 'mileage applies'}
                    </p>
                  </li>)}
              </ul>
            </div>
          </section>

          <section aria-labelledby="trust" className="mt-12" data-id="element-1868" data-mp="2imvlj5x67js7">
            <SectionHeading level={2} title="Checks and credentials" data-id="element-1869" data-mp="2inde9skmjfvd" />
            <Card className="mt-5 p-5" data-id="element-1870" data-mp="23qw7lendqpf5">
              <VerificationList items={chef.verifications} data-id="element-1871" data-mp="23s77329slb0g" />
            </Card>
          </section>

          <section aria-labelledby="reviews" className="mt-12" data-id="element-1872" data-mp="28qs9978lu4rj">
            <SectionHeading level={2} title="Reviews" hint="Scored on four things separately, by guests who completed a booking." data-id="element-1873" data-mp="1p3xg5joivv0f" />
            <Card className="mt-5 p-5" data-id="element-1874" data-mp="1l3x1pz1fbjtd">
              <ScoreBreakdown chefId={chef.id} rating={chef.rating} count={chef.reviewCount} data-id="element-1875" data-mp="1z1lm75rkwoxw" />
            </Card>
            <div className="mt-5" data-id="element-1876" data-mp="1u17andqrqwv4">
              {reviews.length ? <ReviewList reviews={reviews} data-id="element-1877" data-mp="281rtub0liup5" /> : <p className="text-sm text-ink-2" data-id="element-1878" data-mp="27zxd78wcxoqv">No written reviews yet — this chef is new to Pan &amp; Apron.</p>}
            </div>
          </section>
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start" data-id="element-1879" data-mp="2hrbcpgob6ok0">
          <Card className="p-5" data-id="element-1880" data-mp="2csgm3sk3nvpc">
            <p data-id="element-1881" data-mp="2ct9usz2mcva4">
              <span className="text-[13px] text-ink-2" data-id="element-1882" data-mp="2cutcmu3xbee0">{t('common.from')} </span>
              <span className="tnum font-display text-3xl font-semibold tracking-[-0.02em] text-ink" data-id="element-1883" data-mp="2rqivwkhq5df1">
                {currency(price, locale, currencyCode)}
              </span>
              <span className="text-[13px] text-ink-2" data-id="element-1884" data-mp="2tv5r75ik0btg"> / {unit === 'guest' ? t('common.perGuest') : t('common.perPortion')}</span>
            </p>
            <p className="mt-1 text-[13px] text-ink-3" data-id="element-1885" data-mp="2yqupz6mr1oop">
              Minimum spend {currency(chef.minimumSpend, locale, currencyCode)} · {chef.depositPercent}% deposit
            </p>

            <div className="mt-5 space-y-2" data-id="element-1886" data-mp="38l3gxpw4t9kr">
              <ButtonLink to={`/request/${chef.slug}`} variant="action" size="lg" full data-id="element-1887" data-mp="3dimyayo4m2gx">
                {t('common.request')}
              </ButtonLink>
              <ButtonLink to={existingThread ? `/app/inbox/${existingThread.id}` : `/request/${chef.slug}`} variant="outline" size="lg" full data-id="element-1888" data-mp="3hlrwwq535k0a">
                <MessageSquareIcon className="h-4 w-4" aria-hidden="true" data-id="element-1889" data-mp="1s4qv5qoeqpf6" />
                {t('common.message')}
              </ButtonLink>
            </div>

            <dl className="mt-5 border-t border-hairline pt-4 text-sm" data-id="element-1890" data-mp="1x4dgknee3bz2">
              <div className="flex items-start justify-between gap-3 py-2" data-id="element-1891" data-mp="1pkkt5ojct919">
                <dt className="flex items-center gap-1.5 text-ink-2" data-id="element-1892" data-mp="1ktvxry4jzrwu">
                  <CalendarCheckIcon className="h-4 w-4 text-ink-3" aria-hidden="true" data-id="element-1893" data-mp="1krjar6jxurcx" /> Next available
                </dt>
                <dd className="text-right font-medium text-ink" data-id="element-1894" data-mp="1avolvtp4g01p">{longDate(chef.nextAvailable, locale)}</dd>
              </div>
              <div className="flex items-start justify-between gap-3 py-2" data-id="element-1895" data-mp="28i5fanuqhti6">
                <dt className="text-ink-2" data-id="element-1896" data-mp="23kdf76jxr12w">Booking</dt>
                <dd className="text-right font-medium text-ink" data-id="element-1897" data-mp="23kvfqfdms1gm">
                  {chef.bookingMode === 'instant' ? 'Instant on open dates' : 'Request to book'}
                </dd>
              </div>
              <div className="flex items-start justify-between gap-3 py-2" data-id="element-1898" data-mp="1tlo1zr2zi4sz">
                <dt className="text-ink-2" data-id="element-1899" data-mp="n6n61ldylmme">Cancellation</dt>
                <dd className="text-right text-[13px] font-medium text-ink" data-id="element-1900" data-mp="i70sfau7y4d2">{cancellationCopy[chef.cancellationTier]}</dd>
              </div>
            </dl>

            <div className="mt-4 flex items-start gap-2.5 rounded-lg bg-primary-soft p-3.5" data-id="element-1901" data-mp="26znc1kdkj4xj">
              <ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" data-id="element-1902" data-mp="2bszn0rpbrd7t" />
              <p className="text-[13px] leading-relaxed text-primary" data-id="element-1903" data-mp="1x4lmwojam2uy">
                Your allergy record travels with the request, and {chef.name.split(' ')[0]} has to acknowledge it before quoting.
              </p>
            </div>

            <p className="mt-4 text-[12px] text-ink-3" data-id="element-1904" data-mp="2uq97mc6dywqn">
              Dietary capability:{' '}
              {chef.dietaryCapabilities.join(', ') || 'not specified'}.{' '}
              <Link to="/app/account/dietary" className="font-medium text-primary underline underline-offset-2" data-id="element-1905" data-mp="2fvcqf9glmz1f">
                Check against your profile
              </Link>
            </p>
          </Card>
        </aside>
      </div>
    </AppShell>;
}